import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Mic, MicOff, Video, VideoOff } from 'lucide-react';
import DailyIframe from '@daily-co/daily-js';
import { useWebSocket } from '../../../WebSocketContext';
import logger from './../../../utils/logger.js';


const BotInteractionStep = ({ interviewId, setPermissionStatus }) => {
  const [status, setStatus] = useState('initializing'); // initializing, connecting, connected, error
  const [errorMessage, setErrorMessage] = useState('');
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [botMessage, setBotMessage] = useState('');
  const [userAudioLevel, setUserAudioLevel] = useState(0);
  const [botAudioLevel, setBotAudioLevel] = useState(0);
  const [userWaveform, setUserWaveform] = useState(Array(32).fill(0));
  const [botWaveform, setBotWaveform] = useState(Array(32).fill(0));
  const [isBotSpeaking, setIsBotSpeaking] = useState(false);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [botInteractionEventSent, setBotInteractionEventSent] = useState(false);
  
  const callFrameRef = useRef(null);
  const containerRef = useRef(null);
  const roomUrlRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const audioLevelTimerRef = useRef(null);
  
  // Get WebSocket context
  const { sendBotInteractionSuccess } = useWebSocket();
  const BASE_URL = import.meta.env.VITE_APP_BASE_URL;
  
  // Clean up any existing Daily instances to prevent duplicate errors
  const cleanupExistingDailyInstances = () => {
    if (window._dailyCallObject) {
      try {
        window._dailyCallObject.destroy();
      } catch (err) {
        logger.debug('Error cleaning up existing Daily instance:', err);
      }
      window._dailyCallObject = null;
    }
  };
  
  // Setup audio visualization
  const setupAudioVisualization = useCallback(() => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }

      // Create a real-time audio analyzer for microphone input
      const setupMicrophoneAnalyzer = async () => {
        try {
          // Get user's microphone stream
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          const micSource = audioContextRef.current.createMediaStreamSource(stream);
          
          // Create analyzer for microphone
          const micAnalyser = audioContextRef.current.createAnalyser();
          micAnalyser.fftSize = 256;
          micAnalyser.smoothingTimeConstant = 0.8;
          micSource.connect(micAnalyser);
          
          return {
            analyser: micAnalyser,
            stream: stream
          };
        } catch (err) {
          logger.error('Error accessing microphone:', err);
          return null;
        }
      };
      
      // Initialize bot waveform animation
      let botAnimationActive = false;
      let botWaveAmplitude = 0;
      let botWaveFrequency = 1;
      let lastBotUpdateTime = Date.now();
      
      // Function to update bot waveform parameters
      const updateBotWaveParameters = () => {
        const now = Date.now();
        const elapsed = (now - lastBotUpdateTime) / 1000;
        lastBotUpdateTime = now;
        
        if (isBotSpeaking) {
          // Gradually increase amplitude when bot starts speaking
          botWaveAmplitude = Math.min(1, botWaveAmplitude + elapsed * 2);
          
          // Randomly change frequency for more natural speech pattern
          if (Math.random() < 0.05) {
            botWaveFrequency = 0.8 + Math.random() * 1.5;
          }
        } else {
          // Gradually decrease amplitude when bot stops speaking
          botWaveAmplitude = Math.max(0, botWaveAmplitude - elapsed * 2);
        }
        
        // If bot is actively speaking or fading out, keep animation active
        botAnimationActive = botWaveAmplitude > 0.01;
        
        return botAnimationActive;
      };
      
      // Function to generate bot waveform
      const generateBotWaveform = () => {
        const time = Date.now() / 1000;
        const botLevel = botWaveAmplitude * (0.5 + 0.5 * Math.sin(time * botWaveFrequency));
        setBotAudioLevel(botLevel);
        
        // Generate waveform with multiple harmonics for natural speech
        return Array(32).fill(0).map((_, i) => {
          const x = i / 32;
          // Mix multiple frequencies with different phases
          return (
            (Math.sin(x * 10 + time * 5) * 0.3) + 
            (Math.sin(x * 5 + time * 3) * 0.4) + 
            (Math.sin(x * 20 + time * 8) * 0.2) + 
            (Math.sin(x * 3 + time * 2) * 0.1)
          ) * botWaveAmplitude * (0.5 + 0.5 * Math.sin(time * botWaveFrequency * 2 + i * 0.2));
        });
      };
      
      // Set up microphone analyzer
      setupMicrophoneAnalyzer().then(micSetup => {
        if (micSetup) {
          const { analyser: micAnalyser, stream: micStream } = micSetup;
          
          // Update function to process audio data
          const updateAudioVisualization = () => {
            // Process microphone input for user
            if (micAnalyser) {
              const micBufferLength = micAnalyser.frequencyBinCount;
              const micDataArray = new Uint8Array(micBufferLength);
              micAnalyser.getByteFrequencyData(micDataArray);
              
              // Calculate average level for microphone
              let micSum = 0;
              for (let i = 0; i < micBufferLength; i++) {
                micSum += micDataArray[i];
              }
              const micAvg = micSum / micBufferLength;
              const micLevel = Math.min(1, micAvg / 128);
              
              // Update user audio level and detect if speaking
              setUserAudioLevel(micLevel);
              setIsUserSpeaking(micLevel > 0.05);
              
              // Generate waveform data for user
              if (micLevel > 0.05) {
                const newUserWaveform = Array(32).fill(0).map((_, i) => {
                  // Use actual frequency data for a more accurate visualization
                  const dataIndex = Math.floor(i * (micBufferLength / 32));
                  return (micDataArray[dataIndex] / 255) * micLevel;
                });
                setUserWaveform(newUserWaveform);
              }
            }
            
            // Update bot waveform parameters
            updateBotWaveParameters();
            
            // Generate and update bot waveform
            const newBotWaveform = generateBotWaveform();
            setBotWaveform(newBotWaveform);
            
            // Continue animation
            animationFrameRef.current = requestAnimationFrame(updateAudioVisualization);
          };
          
          // Start the visualization loop
          animationFrameRef.current = requestAnimationFrame(updateAudioVisualization);
          
          // Store the microphone stream for cleanup
          return () => {
            if (micStream) {
              micStream.getTracks().forEach(track => track.stop());
            }
          };
        }
      }).catch(err => {
        logger.error('Error setting up microphone analyzer:', err);
      });
    } catch (error) {
      logger.error('Error setting up audio visualization:', error);
    }
  }, [isBotSpeaking]);
  
  // Detect bot speech activity from Daily.co call
  const setupSpeechDetection = useCallback(() => {
    if (!callFrameRef.current) return;
    
    // Set up a timer to check audio levels
    audioLevelTimerRef.current = setInterval(() => {
      if (!callFrameRef.current) return;
      
      const participants = callFrameRef.current.participants();
      
      // Check if the bot participant is speaking
      const botParticipant = Object.values(participants).find(p => 
        p.session_id !== participants.local.session_id
      );
      
      if (botParticipant) {
        const botAudioLevel = botParticipant.audioLevel || 0;
        setIsBotSpeaking(botAudioLevel > 0.05);
      }
    }, 100);
  }, []);

  useEffect(() => {
    // Fetch the test room URL
    const fetchTestRoomUrl = async () => {
      try {
        setStatus('initializing');
        const response = await fetch(`https://${BASE_URL}/api/interview/run_test/${interviewId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyX2lkIjoiNjdiOWI5N2U1MTRhNWY1M2YxMjVjZGMyIiwidXNlcl9uYW1lIjoiQWJoaXNoZWsiLCJvcmdfaWRzIjpbIjY3MmI5MjQzYWMwOTU4NmFmYTI2NGRjNCJdLCJleHAiOjE3NDYwNDAxNjF9.zJDXDYDBV0WQpMvOkMF459CBLaDwzulTPlU-uzMqOXw'
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to get test room URL');
        }
        
        const data = await response.json();
        roomUrlRef.current = data.room_url;
        
        // Initialize the Daily call
        initializeCall();
      } catch (error) {
        logger.error('Error fetching test room URL:', error);
        setStatus('error');
        setErrorMessage('Failed to get test room URL. Please try again.');
      }
    };
    
    fetchTestRoomUrl();
    
    return () => {
      // Clean up on unmount
      if (callFrameRef.current) {
        callFrameRef.current.destroy();
        callFrameRef.current = null;
      }
      
      cleanupExistingDailyInstances();
      
      // Clean up audio visualization
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      
      if (audioLevelTimerRef.current) {
        clearInterval(audioLevelTimerRef.current);
      }
      
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(logger.error);
      }
    };
  }, [interviewId, setupSpeechDetection]);
  
  const initializeCall = () => {
    try {
      // Clean up any existing instances first
      cleanupExistingDailyInstances();
      
      if (!roomUrlRef.current) {
        setStatus('error');
        setErrorMessage('No room URL available');
        return;
      }
      
      setStatus('connecting');
      
      // Create a unique container ID to avoid conflicts
      const containerId = `daily-container-${Date.now()}`;
      if (containerRef.current) {
        containerRef.current.id = containerId;
      }
      
      // Create the Daily iframe but make it hidden
      const callFrame = DailyIframe.createFrame({
        showLeaveButton: false,
        iframeStyle: {
          width: '1px',  // Make it tiny
          height: '1px', // Make it tiny
          border: '0',
          position: 'absolute',
          left: '-9999px', // Position off-screen
          top: '-9999px',  // Position off-screen
          opacity: '0.01', // Nearly invisible but still functional
        },
        dailyConfig: {
          experimentalChromeVideoMuteLightOff: true,
        },
      });
      
      // Store the call object globally to prevent duplicates
      window._dailyCallObject = callFrame;
      callFrameRef.current = callFrame;
      
      // Set up event listeners
      callFrame
        .on('joined-meeting', handleJoinedMeeting)
        .on('error', handleError)
        .on('participant-joined', handleParticipantJoined)
        .on('participant-left', handleParticipantLeft)
        .on('app-message', handleAppMessage);
      
      // Join the meeting
      callFrame.join({
        url: roomUrlRef.current,
        showLeaveButton: false,
      });
      
    } catch (error) {
      // logger.error('Error initializing call:', error);
      // setStatus('error');
      // setErrorMessage(`Failed to initialize video call: ${error.message}`);
    }
  };
  
  const handleJoinedMeeting = () => {
    setStatus('connected');
    // Update permission status for the parent component
    setPermissionStatus(prev => ({
      ...prev,
      botInteraction: 'granted'
    }));
    
    // Send bot interaction success event
    if (!botInteractionEventSent) {
      sendBotInteractionSuccess();
      setBotInteractionEventSent(true);
    }
    
    // Force bot to speak immediately to show visualization
    setIsBotSpeaking(true);
    
    // Set up audio visualization and speech detection
    setupAudioVisualization();
    setupSpeechDetection();
    
    // Send a welcome message to the bot with a guaranteed speech animation
    const runBotSpeechSequence = () => {
      // First message
      setBotMessage('Hello! I am Sarah. Please say "Hello" to test your audio.');
      
      // Ensure bot is speaking for this message
      setIsBotSpeaking(true);
      
      // Create a reliable speech pattern
      const speechPattern = [
        { speak: false, delay: 2500 },  // Pause after first sentence
        { speak: true, delay: 3000 },   // Speak second sentence
        { speak: false, delay: 1000 },  // Short pause
        { speak: true, delay: 2000 },   // Speak third sentence
        { speak: false, delay: 1500 },  // Pause before next message
        { speak: true, delay: 3500 }    // Final part of first message
      ];
      
      // Execute the speech pattern
      let cumulativeDelay = 0;
      speechPattern.forEach(step => {
        setTimeout(() => {
          setIsBotSpeaking(step.speak);
        }, cumulativeDelay);
        cumulativeDelay += step.delay;
      });
      
      // Second message after the first sequence completes
      setTimeout(() => {
        setBotMessage('I can hear you clearly. Please continue speaking to test your microphone.');
        setIsBotSpeaking(true);
        
        // Another speech pattern for second message
        setTimeout(() => setIsBotSpeaking(false), 2000);
        setTimeout(() => setIsBotSpeaking(true), 3000);
        setTimeout(() => setIsBotSpeaking(false), 5000);
      }, cumulativeDelay + 1000);
    };
    
    // Start the bot speech sequence after a short delay
    setTimeout(runBotSpeechSequence, 1000);
  };
  
  const handleError = (error) => {
    logger.error('Daily error:', error);
    setStatus('error');
    setErrorMessage(`Video call error: ${error.errorMsg || 'Unknown error'}`);
    
    // Update permission status for the parent component
    setPermissionStatus(prev => ({
      ...prev,
      botInteraction: 'denied'
    }));
  };
  
  const handleParticipantJoined = (event) => {
    logger.debug('Participant joined:', event.participant);
    if (event.participant.session_id !== callFrameRef.current.participants().local.session_id) {
      setBotMessage('Hello! I am Sarah. Please say "Hello" to test your audio.');
    }
  };
  
  const handleParticipantLeft = (event) => {
    logger.debug('Participant left:', event.participant);
  };
  
  const handleAppMessage = (event) => {
    logger.debug('App message received:', event);
    if (event.data && event.data.message) {
      setBotMessage(event.data.message);
    }
  };
  
  const toggleMute = () => {
    if (callFrameRef.current) {
      const newMuteState = !isMuted;
      callFrameRef.current.setLocalAudio(!newMuteState);
      setIsMuted(newMuteState);
    }
  };
  
  const toggleVideo = () => {
    if (callFrameRef.current) {
      const newVideoState = !isVideoOff;
      callFrameRef.current.setLocalVideo(!newVideoState);
      setIsVideoOff(newVideoState);
    }
  };
  
  const leaveCall = () => {
    if (callFrameRef.current) {
      callFrameRef.current.leave();
      callFrameRef.current.destroy();
      callFrameRef.current = null;
      window._dailyCallObject = null;
    }
    setStatus('initializing');
  };
  
  const retryConnection = () => {
    leaveCall();
    initializeCall();
  };
  
  // Render audio visualization waveform
  const renderWaveform = (waveform, level, color, label) => {
    return (
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-gray-600">{label}</span>
          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className={`h-full ${color}`} 
              style={{ width: `${Math.max(5, level * 100)}%` }}
            ></div>
          </div>
        </div>
        <svg width="100%" height="60" viewBox="0 0 300 60" className="mx-auto">
          {/* Define gradients for the waveforms */}
          <defs>
            <linearGradient id={`gradient-${label}`} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={color === 'bg-indigo-500' ? '#6366F1' : '#10B981'} />
              <stop offset="100%" stopColor={color === 'bg-indigo-500' ? '#A5B4FC' : '#6EE7B7'} />
            </linearGradient>
            
            {/* Add glow effect */}
            <filter id={`glow-${label}`} x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="2" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
          </defs>
          
          {/* Horizontal center line */}
          <line x1="0" y1="30" x2="300" y2="30" stroke="#E5E7EB" strokeWidth="1" />
          
          {/* Waveform */}
          <path
            d={`M 0,30 ${waveform.map((point, i) => {
              const x = (i / (waveform.length - 1)) * 300;
              // Amplify the wave based on audio level
              const amplifier = 25 * (0.1 + level * 2);
              const y = 30 - (point * amplifier);
              return `L ${x},${y}`;
            }).join(' ')}`}
            stroke={`url(#gradient-${label})`}
            strokeWidth="2"
            fill="none"
            filter={`url(#glow-${label})`}
          />
          
          {/* Mirror the waveform */}
          <path
            d={`M 0,30 ${waveform.map((point, i) => {
              const x = (i / (waveform.length - 1)) * 300;
              // Amplify the wave based on audio level and mirror it
              const amplifier = 25 * (0.1 + level * 2);
              const y = 30 + (point * amplifier);
              return `L ${x},${y}`;
            }).join(' ')}`}
            stroke={`url(#gradient-${label})`}
            strokeWidth="2"
            fill="none"
            filter={`url(#glow-${label})`}
          />
        </svg>
      </div>
    );
  };
  
  return (
    <div className="flex flex-col items-center w-full">
      <h2 className="text-md font-semibold text-gray-900 mb-4">Test Bot Interaction</h2>
      <p className="text-sm text-gray-600 mb-4 text-center">
        Let's test your interaction with our AI interviewer. This will ensure your audio is working properly.
      </p>
      
      {/* Hidden container for Daily iframe */}
      <div ref={containerRef} className="hidden"></div>
      
      <div className="w-full max-w-md mx-auto mb-4">
        {status === 'initializing' || status === 'connecting' ? (
          <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-600 mb-2"></div>
            <p className="text-sm text-gray-600">
              {status === 'initializing' ? 'Initializing test...' : 'Connecting to test interview...'}
            </p>
          </div>
        ) : status === 'error' ? (
          <div className="flex flex-col items-center justify-center p-8 bg-red-50 rounded-lg">
            <XCircle size={32} className="text-red-500 mb-2" />
            <p className="text-sm text-red-600 mb-2 text-center">{errorMessage}</p>
            <button
              className="px-3 py-1 bg-indigo-600 text-white rounded-md text-sm"
              onClick={retryConnection}
            >
              Retry Connection
            </button>
          </div>
        ) : (
          <div className="p-4 bg-gray-50 rounded-lg">
            {/* Bot audio visualization */}
            {/* <div className="mb-4">
              <div className="flex items-center mb-2">
                <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center mr-2">
                  <span className="text-indigo-600 text-xs font-bold">AI</span>
                </div>
                <span className="text-sm font-medium text-gray-700">AI Interviewer</span>
                {isBotSpeaking && (
                  <span className="ml-2 px-1.5 py-0.5 text-xs bg-indigo-100 text-indigo-700 rounded-full">Speaking</span>
                )}
              </div>
              {renderWaveform(botWaveform, botAudioLevel, 'bg-indigo-500', 'bot')}
            </div> */}
            
            {/* User audio visualization */}
            <div>
              <div className="flex items-center mb-2">
                <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mr-2">
                  <Mic size={12} className="text-green-600" />
                </div>
                <span className="text-sm font-medium text-gray-700">You</span>
                {isUserSpeaking && (
                  <span className="ml-2 px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">Speaking</span>
                )}
              </div>
              {renderWaveform(userWaveform, userAudioLevel, 'bg-green-500', '')}
            </div>
          </div>
        )}
        
        {/* Controls */}
        {/* {status === 'connected' && (
          <div className="mt-4 flex justify-center space-x-4">
            <button
              className={`p-2 rounded-full ${isMuted ? 'bg-red-100' : 'bg-gray-100'}`}
              onClick={toggleMute}
              title={isMuted ? "Unmute" : "Mute"}
            >
              {isMuted ? <MicOff size={20} className="text-red-600" /> : <Mic size={20} className="text-gray-700" />}
            </button>
          </div>
        )} */}
      </div>
      
      {/* Bot message */}
      {botMessage && status === 'connected' && (
        <div className="p-3 bg-indigo-50 text-indigo-700 rounded-md w-full max-w-md text-sm mb-4">
          <div className="flex items-start">
            <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center mr-3 flex-shrink-0">
              <span className="text-indigo-600 font-bold text-xs">AI</span>
            </div>
            <p className="text-sm">{botMessage}</p>
          </div>
        </div>
      )}
      
      {/* Status indicator */}
      {status === 'connected' ? (
        <div className="flex items-center p-3 bg-green-50 text-green-700 rounded-md text-sm">
          <CheckCircle size={20} className="mr-2 flex-shrink-0" />
          <span>Connection successful! You can now interact with the AI interviewer.</span>
        </div>
      ) : status === 'error' ? (
        <div className="flex items-center p-3 bg-red-50 text-red-700 rounded-md text-sm">
          <XCircle size={20} className="mr-2 flex-shrink-0" />
          <span>Connection failed. Please check your internet connection and try again.</span>
        </div>
      ) : (
        <div className="flex items-center p-3 bg-yellow-50 text-yellow-700 rounded-md text-sm">
          <AlertTriangle size={20} className="mr-2 flex-shrink-0" />
          <span>{status === 'initializing' ? 'Initializing test...' : 'Connecting to test interview...'}</span>
        </div>
      )}
    </div>
  );
};

export default BotInteractionStep;